import { useState, type ReactNode } from "react";
import { Crown } from "lucide-react";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { useToast } from "@/hooks/use-toast";
import { usePrices, formatPlanLine } from "@/hooks/usePrices";
import type { PaywallSource } from "@/lib/paywallSource";

// v1.1.2-b5: inline lock card placed at the point of friction inside each
// gated surface. Replaces the old padlock-icon / redirect pattern with an
// honest preview + single-button upgrade path. Never lives in a header,
// sheet, or settings tab.
//
// Pro is the only product. Price strings come from RevenueCat via usePrices,
// never hardcoded. Reads session.tier through hasProAccess — the one canonical
// Pro rule. (A second product path existed for the one-time Transfer Organiser
// unlock; that IAP was withdrawn on 2026-07-15 and Storage Maximisation is a
// Pro feature now, so the branch is gone.)
//
// Pass children to render the actual feature when the entitlement is held;
// the ProLock will render those instead of the lock card.
//
// Brand: Nexus Dex neon-green (#00FF88) on black. No GoDexAI references,
// no em dashes, no emojis. Friendly kid-safe tone.

interface ProLockProps {
  feature: string;
  blurb: string;
  /**
   * WHERE this lock is, for the admin Pro hot-spots table.
   *
   * WHY IT IS A PROP AND NOT `feature` (2026-09-03)
   *   This card hardcoded openPaywall("pro-lock", feature) and `feature` is a
   *   DISPLAY string that never leaves the device — the paywall-intent POST
   *   only carries `source`. So all nine ProLock call sites reported as one
   *   "Pro lock (generic)" row, and the dashboard could not say which wall
   *   anyone hit. Worse, a feature with a second non-ProLock entry point got
   *   SPLIT across two rows: Calendar's event-dex-gaps lock (4 trainers, and
   *   the only ProLock purchase on record) sat in "generic" while
   *   EventDexGaps.tsx's own button reported properly, so the one feature
   *   converting best looked like two also-rans.
   *
   *   Optional, defaulting to "pro-lock", so adding a lock without thinking
   *   about attribution still records something. It should stay near zero.
   */
  source?: PaywallSource;
  children?: ReactNode;
}

// b19: NEON is a literal hex retained for alpha-suffix composition on the
// card halo (border / faint background). For the actual Upgrade to Pro CTA
// fill below, use var(--accent-fill) / var(--accent-fill-hover) so it
// matches the canonical Nexus green used elsewhere in the app.
const NEON = "#00FF88";

export function ProLock({ feature, blurb, source = "pro-lock", children }: ProLockProps) {
  const { tier, openPaywall } = useSession();
  const { prices } = usePrices();

  const unlocked = hasProAccess(tier);

  if (unlocked && children) return <>{children}</>;
  if (unlocked) return null;

  return (
    <div
      data-testid={`pro-lock-${feature}`}
      className="prolock-card rounded-2xl p-5 flex flex-col items-center gap-3 text-center"
      style={{
        border: `1px solid ${NEON}66`,
        background: `linear-gradient(180deg, ${NEON}14 0%, ${NEON}05 100%)`,
        boxShadow: `0 0 0 1px ${NEON}1f inset`,
      }}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{ background: `${NEON}1f`, border: `1px solid ${NEON}55` }}
      >
        <Crown className="w-5 h-5" style={{ color: NEON }} />
      </div>
      <p className="text-sm font-semibold max-w-xs prolock-blurb">{blurb}</p>
      <button
        type="button"
        onClick={() => openPaywall(source, feature)}
        data-testid={`button-unlock-${feature}`}
        className="rounded-full px-5 py-2 text-xs font-bold transition-transform active:scale-95"
        style={{
          background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
          color: "var(--text-on-accent)",
          boxShadow: "0 0 10px var(--accent-fill-glow)",
        }}
      >
        Upgrade to Pro
      </button>
      {/* Only show the price line once a real price has resolved. Until
          offerings load — and on any build without configured in-app purchases
          (e.g. Android before Play Billing / RevenueCat is wired) — both prices
          are null, so we hide the line rather than render "From .../mo or .../yr". */}
      {(prices.monthly || prices.annual) && (
        <p className="text-[10px] prolock-footnote">
          From {formatPlanLine(prices.monthly, "/mo")} or {formatPlanLine(prices.annual, "/yr")}
        </p>
      )}
    </div>
  );
}

export default ProLock;
